import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ClipboardList, CheckSquare, Users, ChevronDown, Bell, ListChecks } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LandingHeader } from '../../components/landing/LandingHeader';
import { CTASection } from '../../components/landing/CTASection';

export default function TaskManagement() {
  useEffect(() => {
    document.title = 'Employee Task Management Software | ReStaff';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Assign, track, and manage employee tasks efficiently with ReStaff.';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="task-management-page">
      <LandingHeader />

      {/* HERO */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Manrope' }}>
            Employee Task Management Software
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Task management software is part of workforce management systems that helps businesses assign, track, and manage employee tasks efficiently.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white text-base px-8" data-testid="task-hero-cta">
                Start Free Trial <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
          <p className="mt-3 text-sm text-slate-400">30-day free trial &middot; Cancel anytime, no questions asked</p>
        </div>
      </section>

      {/* WHAT IT DOES */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5" style={{ fontFamily: 'Manrope' }}>
            What is Employee Task Management?
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Employee task management is the process of assigning, tracking, and completing operational tasks across your team. ReStaff provides a built-in task system so managers can create daily checklists, assign work to specific employees, and monitor completion — all from the same platform they use for scheduling and time tracking.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Create & Assign Tasks', desc: 'Managers create tasks with a title, description, and due date. Assign each task to a specific employee.', icon: ClipboardList },
              { step: '2', title: 'Employees Complete Work', desc: 'Staff see their assigned tasks in the employee portal. They mark tasks as complete when finished.', icon: CheckSquare },
              { step: '3', title: 'Track & Verify', desc: 'Managers monitor completion in real time. Overdue and incomplete tasks are easy to spot and follow up on.', icon: ListChecks },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.step} className="bg-white border border-slate-100 rounded-xl p-6 text-center" data-testid={`task-step-${s.step}`}>
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Task Management Features
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
            {[
              { title: 'Assign Tasks to Employees', desc: 'Create a task and assign it to a specific staff member. They see it immediately in their portal — no verbal instructions needed.', icon: Users },
              { title: 'Track Completion Status', desc: 'See which tasks are pending, in progress, or completed. Overdue tasks are flagged so nothing falls through the cracks.', icon: CheckSquare },
              { title: 'Daily Checklists', desc: 'Create standard checklists for opening, closing, cleaning, or safety procedures. Assign them daily to the responsible employee.', icon: ClipboardList },
              { title: 'Team Coordination', desc: 'All tasks are visible to managers in one place. Coordinate daily work across the team without meetings or message threads.', icon: ListChecks },
              { title: 'Due Dates & Priorities', desc: 'Set due dates to keep tasks on schedule. Staff know what to complete first during their shift.', icon: Bell },
              { title: 'Mobile Access', desc: 'Employees view and complete tasks from their phone. Perfect for staff who are always on the move or on the floor.', icon: CheckCircle2 },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white border border-slate-100 rounded-xl p-6 hover:border-orange-200 hover:shadow-sm transition-all" data-testid={`task-feature-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Why Use ReStaff for Task Management
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Eliminate missed tasks with clear assignments and due dates',
              'Save time by replacing verbal instructions with digital tasks',
              'Improve daily operations with consistent checklists',
              'Increase accountability — everyone knows what to do',
              'Track task completion across your entire team',
              'Keep everything in one platform — tasks, schedules, and time tracking',
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-4">
                <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700">{b}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slate-500 text-center leading-relaxed">
            Task management works best alongside ReStaff <Link to="/employee-scheduling" className="text-orange-500 hover:underline font-medium">employee scheduling software</Link>, <Link to="/time-tracking" className="text-orange-500 hover:underline font-medium">time tracking</Link>, <Link to="/timesheet-software" className="text-orange-500 hover:underline font-medium">timesheet software</Link>, and <Link to="/labor-cost-management" className="text-orange-500 hover:underline font-medium">labor cost management</Link>. Used by <Link to="/salons" className="text-orange-500 hover:underline font-medium">salons</Link>, <Link to="/construction" className="text-orange-500 hover:underline font-medium">construction teams</Link>, and more.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              { q: 'What is employee task management software?', a: 'Employee task management software lets managers create, assign, and track tasks for their team. It replaces verbal instructions and paper checklists with a digital system that ensures accountability and completion tracking.' },
              { q: 'How do I manage employee tasks effectively?', a: 'Create clear, specific tasks with due dates. Assign each task to a responsible employee. Monitor completion status daily and follow up on overdue items. ReStaff makes this workflow simple and visible.' },
              { q: 'How do I track task completion?', a: 'In ReStaff, the Tasks page shows all tasks with their current status — pending, completed, or overdue. Employees mark tasks as done from their portal, and managers verify completion.' },
              { q: 'Can tasks be used for daily opening and closing checklists?', a: 'Yes. Create a standard checklist of tasks for opening or closing procedures and assign them to the responsible employee each day. This ensures nothing is missed during shift transitions.' },
            ].map((item, i) => (
              <details key={i} className="group bg-slate-50 border border-slate-100 rounded-xl" data-testid={`task-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-semibold text-slate-900 select-none">
                  {item.q}
                  <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 flex-shrink-0 ml-3" />
                </summary>
                <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
